// Справка „По групи" (Справки → categories; в кода — „категории").
//
// Същите данни като „По артикули" (`GET /reports/analytics/ordered-items`), но
// погледнати отгоре: целият период се тегли наведнъж и се групира по категория
// в браузъра — ендпойнтът не знае да агрегира по категория. Затова таблицата е
// в КЛИЕНТСКИ режим: сортирането, филтърът и редът „Общо" се смятат тук.
//
// Името на категорията отваря „По артикули" с ?category=<id> — оттам
// нататък въпросът „кое в тази категория се продава" е на съседната справка.
// Кликът върху останалата част от реда не прави нищо.

import { useCallback, useEffect, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { CategoriesKpis } from "./categories/CategoriesKpis";
import { useCategoriesColumns } from "./categories/categoriesColumns";
import { exportCategories } from "./categories/categoriesExport";
import { LunchScopeToggle } from "./LunchScopeToggle";
import { setLunchScopeParam, type LunchScope } from "./lunchScope";
import { useCategoryRows } from "./categories/useCategoryRows";
import type { CategoryRow } from "./categories/categoriesMath";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

/** Ключът, под който таблицата пази подредбата на колоните на потребителя. */
const CATEGORIES_TABLE_ID = "ordered-categories";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const CategoriesReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const navigate = useNavigate();
  const location = useLocation();
  const langCode = i18n.language?.startsWith("en") ? "en" : "bg";

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  // „Всички / Без обедно / Само обедно" — пресява изтеглените редове
  const [lunchScope, setLunchScope] = useState<LunchScope>("all");
  // Редовете СЛЕД филтър и подредба — Excel изнася видяното, не суровия набор.
  const [visibleRows, setVisibleRows] = useState<CategoryRow[]>([]);

  const { rows, totals, previousTotals, hasLunch, isLoading, isFetching, refetch } =
    useCategoryRows({ dateRange, compare, langCode, lunchScope });
  // Превключвателят е само за период с обедни продажби; без тях — „Всички"
  useEffect(() => {
    if (!isLoading && !hasLunch) setLunchScope("all");
  }, [isLoading, hasLunch]);

  // Артикулите без категория са в кофа с тире — за тях няма id, което да се
  // прати нататък, затова името им не е линк.
  const openCategoryItems = useCallback(
    (row: CategoryRow) => {
      if (row.categoryId === null) return;
      const search = new URLSearchParams(location.search);
      search.set("category", String(row.categoryId));
      setLunchScopeParam(search, row.isLunch, hasLunch);
      navigate({ pathname: "/reports/assortment", search: search.toString() });
    },
    [location.search, navigate, hasLunch],
  );

  // Стабилна, иначе колоните (а с тях и цялата таблица) се пресъздават на
  // всеки рендер само защото функцията в клетката е нова.
  const columns = useCategoriesColumns({
    showDelta: compare,
    onOpenItems: openCategoryItems,
  });

  // Стабилна функция — иначе ефектът в таблицата се пуска на всеки рендер.
  const handleFilteredRows = useCallback(
    (next: CategoryRow[]) => setVisibleRows(next),
    [],
  );

  const handleExport = () =>
    exportCategories(visibleRows, {
      t,
      reportName: t("reportsPage.categoriesReport"),
    });

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.categoriesReport")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={refetch}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
            {hasLunch ? (
              <LunchScopeToggle value={lunchScope} onChange={setLunchScope} />
            ) : null}
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <CategoriesKpis
                totals={totals}
                previous={previousTotals}
                isNarrow={narrow}
              />

              <DataTable<CategoryRow>
                tableId={CATEGORIES_TABLE_ID}
                data={rows}
                columns={columns}
                getRowId={(row) =>
                  `${row.categoryId === null ? "none" : String(row.categoryId)}${
                    row.isLunch ? ":lunch" : ""
                  }`
                }
                refreshing={isFetching}
                ariaLabel={t("reportsPage.categoriesReport")}
                title={
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                      {t("reportsPage.categoriesCountKicker")}
                    </span>
                    <span className="text-[16px] font-extrabold leading-none text-ink">
                      {pluralLabel(
                        t,
                        visibleRows.length,
                        "reportsPage.categoriesTableCountOne",
                        "reportsPage.categoriesTableCount",
                      )}
                      <span className="font-medium text-muted">
                        {` · ${t("reportsPage.categoriesTableHint")}`}
                      </span>
                    </span>
                  </div>
                }
                onFilteredRowsChange={handleFilteredRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={handleExport}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                emptyTitle={t("reportsPage.assortmentNoItems")}
                emptyHint={t("reportsPage.itemsEmptyHint")}
                initialSorting={[{ id: "revenue", desc: true }]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
